/**
 * catalogRows.js — pricebook + tenant-overlay merge, shared by the Settings
 * "Catalog & Inventory" admin table (QuoteItemsPage) and the Quote Builder's
 * live category/product/accessory droplists (QuoteBuilderPage/AddItemGroups).
 *
 * Extracted from components/settings/QuoteItemsPage.js (IDI-9, #115) under
 * IDI-208/IDI-136 so QuoteBuilderPage can reuse the identical merge logic
 * without importing the whole Settings page component (Slideover,
 * TypeaheadChips, ItemCoveragePanel, etc.) into the Quote Builder bundle.
 * Pure, no React/UI imports — safe to import from either surface.
 */

import { categoryIdFromItemTypeLabel, isPerSqftCategory } from './seedCatalog';

// IDI-208 (2026-09-01 ruling): COVERAGE_SEED deleted. It was confirmed dead
// (0 of 11,180 real pricebook rows matched any of its 4 fake seed-catalog
// part numbers -- cc_2in, fg_r30, cell_r30, kraft_r19 were literal
// pre-IDI-9 static PRODUCTS[] ids, never real catalog SKUs -- this seed
// data never applied to a single real catalog item since the catalog moved
// off the static array). The schema it demonstrated is preserved at
// quote-builder/coverageShapeFixture.js (not consumed here -- reference
// only). Real per-item coverage data exists in the legacy source
// (ItemUsages.csv, 65,884 rows) but was never migrated -- see IDI-221.

// DEF-162: exported for regression tests (VG-13.2).
// IDI-9: the row set is now the pricebook (bidit-dev-pricebook), NOT the ~20
// hardcoded seedCatalog PRODUCTS. Each pricebook row is a shared, read-only
// base; per-tenant edits from settings.quote_items are overlaid by partNumber.
// Overlay-only rows (a tenant added an item not in the pricebook) are appended.
//
// pricebookRows: [{ sku, name, partNumber, itemType, rValue, costCents, isActive }]
// overlays:      settings.quote_items entries, keyed by SKU (PR-33 change)
//                Legacy overlays that predate PR-33 carry no sku field. Those
//                are silently ignored on reload — they were fan-out damage waiting
//                to happen. Overlays this app writes going forward all carry sku.
// Pricebook-stored display fields an overlay can override (IDI-507 (5)).
const STORED_DISPLAY_KEYS = ['salesDescription', 'defaultVendor', 'defaultWarehouse'];

const COVERAGE_KEYS = ['coverage_mode', 'coverage_per_set', 'coverage_max_thickness_in',
  'coverage_rvalues_attic', 'coverage_rvalues_wall', 'coverage_rvalues'];

export const buildCatalogRows = (pricebookRows = [], overlays = []) => {
  const safeOverlays = Array.isArray(overlays) ? overlays : [];
  // PR-33 (309_ §3): key on sku, the genuinely unique field on the pricebook.
  // The prior key `partNumber || id` collided on 4,118 rows (37%) and caused
  // an edit to one row to fan out to all siblings on reload — the bug PR-30
  // warned about. sku eliminates it.
  const edits = new Map(
    safeOverlays
      .filter((i) => i && i.sku)
      .map((i) => [i.sku, i])
  );
  const usedOverlayKeys = new Set();

  // 1. Pricebook rows overlaid with any per-tenant edit.
  const base = (Array.isArray(pricebookRows) ? pricebookRows : []).map((p, idx) => {
    const partNumber = p.partNumber || '';
    const sku = p.sku || '';
    // PR-33 (309_ §3): row `id` is the sku when present (unique + stable). One
    // pricebook row on tenant 270 has no sku; fall back to a positional composite
    // so it still has a unique local id. See PR-29 for why the positional fallback
    // is only acceptable when the record itself carries no better identifier.
    const rowId = sku || `${partNumber}#${idx}`;
    const row = {
      id: rowId,
      // IDI-75 (2026-08-24 ruling): item_type_desc is now backfilled on
      // ~11,178 pricebook base rows (migration/scripts/idi75-pricebook-item-type-desc.py).
      // The backend surfaces it as `itemType` (see cmd/settings/main.go
      // listPricebook) — resolve it to a real categoryId so the catalog can
      // group by category again. Rows with no resolvable label (the rare
      // genuinely-blank-source-label case) keep categoryId undefined, same
      // as before this fix — they still render in the flat list.
      categoryId: categoryIdFromItemTypeLabel(p.itemType),
      name: p.name || '',
      // IDI-507 (5): the stored Sales Description (1,292 of 1,310 active base
      // rows have one). It was hard-coded '' so the sheet showed blank and a
      // save wrote the blank back over it in the overlay.
      salesDescription: p.salesDescription || '',
      itemType: p.itemType || '',
      // IDI-506: a stored '–' is no R-value.
      rValue: p.rValue && p.rValue !== '–' ? p.rValue : '',
      // IDI-338 (2026-09-14): these were hardcoded to '' regardless of what
      // the backend returned, even though listPricebook (cmd/settings/main.go)
      // (IDI-611: qtyOnHand no longer read from the shared row — see below.)
      // has genuinely populated sqftPerUnit/qtyOnHand on ~1,707 real pricebook
      // rows since the 2026-08-07 import (migration/cmd/migrate/main.go reads
      // Item_SqrFtPerUnit/Item_Qty_Onhand). Every base row silently showed
      // blank in Settings -> Catalog & Inventory until a tenant re-entered it
      // via an overlay edit, discarding real imported data on every load.
      // qtyOnHand is intentionally NOT clamped to >= 0 -- it's post-draw
      // remaining stock and CAN legitimately go negative (see
      // QuoteItemsPage.js's Input, which has no min="0").
      // IDI-428: the pricebook stores sq ft as float32 (148.54 reads back as
      // 148.5399932861328). Round to 2 dp for display; it's a measurement.
      squareFeetPerUnit: (p.sqftPerUnit === null || p.sqftPerUnit === undefined) ? '' : Math.round(Number(p.sqftPerUnit) * 100) / 100,
      // Backend returns cost in cents; the table renders cents directly.
      itemCostPerUnit: typeof p.costCents === 'number' ? p.costCents : 0,
      partNumber,
      sku, // PR-33 (309_): carry sku so overlay writes emit it and reload can find it.
      // IDI-611 (D1): QTY on Hand is this dealer's own count only. The shared
      // pricebook row's qtyOnHand was copied by the migration from whichever
      // dealer's legacy row came first, so it is never shown; only the
      // dealer's settings.quote_items overlay (merged below) supplies it.
      qtyOnHand: '',
      // IDI-642 (D3): default vendor / warehouse are this dealer's own only,
      // as QTY on Hand is. The shared row's vendorId / warehouseId are not
      // read (the API no longer sends them); the dealer's overlay, merged
      // below, supplies a saved value, else the sheet shows "—".
      defaultVendor: '',
      defaultWarehouse: '',
      status: p.isActive === false ? 'inactive' : 'active',
      // IDI-338: the RESOLVED coverage (dealer's own -> IDI central -> none)
      // from listPricebook, in the snake_case keys ItemCoveragePanel edits.
      // coverageSource tells the sheet whose value it is showing.
      coverageSource: p.coverageSource || 'none',
      // IDI-441 (S3): IDI has a central coverage for this product, even when
      // the dealer's own is the one shown ("Reset to IDI default").
      ...(p.coverageHasIdiDefault === true ? { coverageHasIdiDefault: true } : {}),
      ...(p.coveragePerSet != null ? { coverage_per_set: p.coveragePerSet } : {}),
      ...(p.coverageMaxThicknessIn != null ? { coverage_max_thickness_in: p.coverageMaxThicknessIn } : {}),
      ...(p.coverageRValuesAttic ? { coverage_rvalues_attic: p.coverageRValuesAttic } : {}),
      ...(p.coverageRValuesWall ? { coverage_rvalues_wall: p.coverageRValuesWall } : {}),
      ...(p.coverageRValues ? { coverage_rvalues: p.coverageRValues } : {}),
    };
    const edit = edits.get(sku);
    if (edit) usedOverlayKeys.add(sku);
    if (!edit) return row;
    // IDI-338: coverage now lives on the pricebook (dealer override -> IDI
    // central), and listPricebook returns it resolved. The old settings
    // overlay's coverage_* keys are a SECOND, stale store (nothing priced
    // from them), so they must never shadow the resolved value. Overlay
    // coverage only fills in when the pricebook has none at all, which
    // preserves an overlay-only chart until it's re-saved (and so moved).
    const merged = { ...row, ...edit, id: rowId, partNumber, sku };
    // IDI-507 (5): saves before IDI-507 wrote these as '' (the row hard-coded
    // them), so every edited item's overlay carries a blank that hid the
    // stored value (measured: all 10 overlay rows on dev). A blank overlay
    // value is "not set", never a mask over the pricebook's own value.
    // IDI-642: vendor / warehouse base values are now always '', so a blank
    // (or null) overlay value normalises to '' rather than leaking through.
    for (const k of STORED_DISPLAY_KEYS) {
      if (edit[k] === '' || edit[k] == null) merged[k] = row[k];
    }
    // IDI-506: an overlay's stored '–' reads as no R-value.
    if (merged.rValue === '–') merged.rValue = '';
    // IDI-441 (S3): IDI's-default flag is the pricebook's answer, never a
    // copy saved into the overlay.
    if (row.coverageHasIdiDefault) merged.coverageHasIdiDefault = true;
    else delete merged.coverageHasIdiDefault;
    if (row.coverageSource && row.coverageSource !== 'none') {
      // IDI-441 (S3): so is whose coverage is shown. An overlay row saved
      // earlier carries the coverageSource of that moment (e.g. 'dealer'
      // before a Reset to IDI default) and must not shadow it.
      merged.coverageSource = row.coverageSource;
      for (const k of COVERAGE_KEYS) {
        if (k in row) merged[k] = row[k];
        else delete merged[k];
      }
    }
    return merged;
  });

  // 2. Overlay-only rows: tenant-added items with no matching pricebook base.
  const custom = safeOverlays.filter((i) => {
    // PR-33 (309_): key by sku when present; legacy overlays without sku are dropped
    // (they can only come from a version that predates this fix, and would fan out
    // on reload if we honoured them).
    if (!i || !i.sku) return false;
    return !usedOverlayKeys.has(i.sku);
  }).map((i) => (i.rValue === '–' ? { ...i, rValue: '' } : i));

  return [...base, ...custom];
};

// IDI-9: compute the settings.quote_items OVERLAY delta from the full rendered
// row set. We must NOT write all 7,534 pricebook rows into settings — only
// rows the tenant actually changed (differ from their pricebook base) plus
// overlay-only custom rows. `baseByPart` maps partNumber -> pristine pricebook
// row (as produced by buildCatalogRows with empty overlays).
export const computeOverlayDelta = (rows = [], baseByPart = new Map()) => {
  const OVERLAY_FIELDS = [
    'name', 'salesDescription', 'itemType', 'categoryId', 'rValue',
    'squareFeetPerUnit', 'itemCostPerUnit', 'partNumber', 'qtyOnHand',
    'defaultVendor', 'defaultWarehouse', 'status', 'areaScope',
    // IDI-507 (3): `unit` (an accessory's Unit) and the coverage keys the
    // sheet actually writes. 'coverage' was listed and nothing writes it, and
    // without 'unit' a Unit-only edit produced no overlay row, so it was lost.
    'unit', 'coverage_mode', 'coverage_rvalues',
    'coverage_per_set', 'coverage_max_thickness_in',
    'coverage_rvalues_attic', 'coverage_rvalues_wall',
  ];
  const out = [];
  for (const row of rows) {
    // PR-32 (308_ §2): key on unique `id` (partNumber#idx), not partNumber.
    // Keying by partNumber collides on 4,118 rows (37%) — each row diffed
    // against the LAST-writer's base, emitting ~2,828 spurious "edits" on
    // any save and PUT /v1/settings returned 500.
    const key = row.id;
    const base = baseByPart.get(key);
    if (!base) {
      // Overlay-only / custom row — always persist.
      out.push(row);
      continue;
    }
    // Persist only if any overlay field diverges from the pricebook base.
    // IDI-507: '' / null / absent are the same "no value" (a base row has no
    // `unit`; the sheet's form has unit ''), so an unchanged save writes nothing.
    const norm = (v) => (v === '' || v === null || v === undefined ? null : v);
    const changed = OVERLAY_FIELDS.some(
      (f) => JSON.stringify(norm(row[f])) !== JSON.stringify(norm(base[f])),
    );
    if (changed) out.push(row);
  }
  return out;
};

// IDI-441 (S1, D1 ruled 2026-10-04): "a save keeps every existing settings
// row unless the user deleted that specific row." A catalog save used to
// rewrite settings.quote_items from computeOverlayDelta over the rows on
// screen, which dropped any stored row that no longer differed from the
// pricebook (1647 and 918 on 10-01), any row with no sku, and any row added
// elsewhere (the builder's custom accessory) since the page loaded.
//
// mergeOverlayRows applies ONE action's changes to the rows as stored now:
//   stored     settings.quote_items read fresh at save time
//   upsert     the rows this action changed (the saved item, Bulk Change /
//              Undo targets, a deactivated row)
//   removeIds  sku/id of rows the user deleted
// A changed row replaces the stored row with the same sku (else id). With no
// stored row, it is appended only when it differs from its pricebook base
// (computeOverlayDelta's comparison), so an unchanged save adds nothing.
// Every other stored row passes through untouched, in its stored order.
const sameRow = (a, b) => {
  if (!a || !b) return false;
  if (a.sku && b.sku) return String(a.sku) === String(b.sku);
  return Boolean(a.id && b.id && String(a.id) === String(b.id));
};

export const mergeOverlayRows = (stored = [], upsert = [], removeIds = [], baseByPart = new Map()) => {
  const removeSet = new Set((Array.isArray(removeIds) ? removeIds : []).filter(Boolean).map(String));
  const isRemoved = (r) => Boolean(r) && (
    (r.sku && removeSet.has(String(r.sku))) || (r.id && removeSet.has(String(r.id))));
  const out = (Array.isArray(stored) ? stored : []).filter((r) => !isRemoved(r));
  for (const row of (Array.isArray(upsert) ? upsert : [])) {
    if (!row || isRemoved(row)) continue;
    const at = out.findIndex((r) => sameRow(r, row));
    if (at >= 0) out[at] = row;
    else if (computeOverlayDelta([row], baseByPart).length > 0) out.push(row);
  }
  return out;
};

// ─── IDI-208/IDI-136: Quote Builder catalog filters ─────────────────────────
//
// QuoteBuilderPage.loadAccessoryCatalog merges the real pricebook with the
// tenant's settings.quote_items overlay via buildCatalogRows, then splits
// the merged rows into the accessory subset (AccessoryPicker) and the
// insulation subset (AddItemGroups' tenant-product droplist merge). Both
// filters are extracted here as pure functions so they're unit-testable
// without mounting the full QuoteBuilderPage component tree.
//
// A row's `categoryId` is computed by buildCatalogRows from the pricebook's
// item_type_desc via categoryIdFromItemTypeLabel -- NOT the old 3-value
// itemType enum ('accessory' | 'spray_foam' | 'blown_insulation') that the
// pre-fix overlay-only read filtered on (that enum is never the shape of a
// real pricebook row's itemType field, which carries the ~20-value client
// category vocabulary, e.g. "ACCESSORIES", "CLOSED CELL SPRAY FOAM").

/** Rows destined for the accessory picker: category is accessories/light_covers, active. */
export const filterAccessoryRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter(
    (i) =>
      i &&
      (i.categoryId === 'accessories' || i.categoryId === 'light_covers') &&
      (i.status || 'active') === 'active'
  );

/**
 * IDI-373 (Adrian ruling 2026-09-24) -- can this row be priced by AREA?
 *
 * Area-priced products are quoted in square feet, so turning a catalog price
 * into a line total requires coverage: cost_per_unit / sqft_per_unit * sqft.
 * Without coverage there is no honest conversion. The earlier behaviour
 * multiplied a per-bag price by sqft directly, which is how 1,000 sqft of a
 * $47.26 bag reached $47,260 instead of $613.77.
 *
 * The ruling is to HIDE these rows from the area-priced picker rather than let
 * a user select one and then block or guess. Census (2026-09-24, measured):
 * 3,195 of 4,259 catalog products (75%) have no usable coverage, so blocking
 * at selection time would have let a user pick three quarters of the catalog
 * and then refuse to price it.
 *
 * Gate is `> 1`, not `> 0`: migrated rows carry a degenerate coverage of
 * exactly 1 where the value never came over (645 rows measured), and treating
 * 1 sqft-per-unit as real coverage reproduces the per-sqft bug exactly.
 *
 * Accessories are NOT filtered by this -- they are quoted per quantity, not by
 * area, and go through filterAccessoryRows / the accessory picker instead.
 *
 * NOTE ON PLACEMENT: this is deliberately NOT folded into
 * filterInsulationRows. That function is the CATEGORY ROUTER (IDI-208/136):
 * it decides accessory-vs-insulation by resolved categoryId, and its tests pin
 * that contract. Mixing a priceability gate into it conflates two concerns and
 * breaks category routing for rows that are correctly insulation but not yet
 * priceable. Callers that populate the area-priced picker compose the two:
 *   filterInsulationRows(rows).filter(hasUsableCoverage)
 */
export const hasUsableCoverage = (row) => {
  if (!row) return false;
  const cov = Number(
    row.squareFeetPerUnit ?? row.sqftPerUnit ?? row.sqft_per_unit
  );
  if (Number.isFinite(cov) && cov > 1) return true;
  // IDI-173 / IDI-338: a blow-in chart or spray-foam per-set value IS usable
  // coverage (the Coverage dropdown picks which entry prices the line). This
  // is what makes OC PROPINK BLOWING WOOL offerable once it has a chart,
  // even though its migrated sq ft per unit is a degenerate 1.
  const anyPositive = (m) => !!m && Object.values(m).some((v) => Number(v) > 0);
  return anyPositive(row.coverage_rvalues_attic) || anyPositive(row.coverage_rvalues_wall)
    || anyPositive(row.coverage_rvalues) || Number(row.coverage_per_set) > 0;
};

/**
 * IDI-373 (ruling widened 2026-09-24) -- does this row have a real price?
 *
 * A dealer price of $0 means NOT SET, not free. The pricebook stores cost on
 * dealer-override rows (36,727 of them) and leaves cost_cents at 0 on 99.6% of
 * catalog rows, so a 0 here is an absent override rather than a giveaway.
 * Offering a $0 product produces a $0 line, which is the same class of wrong
 * answer as the $47,260 bag -- just in the other direction.
 */
export const hasRealPrice = (row) => {
  if (!row) return false;
  const cost = Number(
    row.itemCostPerUnit ?? row.materialCostPerUnit ?? row.cost_cents ?? row.costCents
  );
  return Number.isFinite(cost) && cost > 0;
};

/**
 * IDI-373: is this row an ACCESSORY (quoted per quantity, not by area)?
 * Accessories need a PRICE but NOT coverage -- a box of light covers has no
 * square-foot coverage and never will.
 */
export const isAccessoryRow = (row) =>
  !!row && (row.categoryId === 'accessories' || row.categoryId === 'light_covers');

/**
 * IDI-373: can this row be offered at all?
 *   area-priced items -> coverage > 1 AND price > 0
 *   accessories       -> price > 0 (coverage not required)
 */
/** IDI-499 D2: AeroBarrier / Removal rows, priced per sq ft (no coverage). */
export const isPerSqftRow = (row) => !!row && isPerSqftCategory(row.categoryId);

export const isOfferable = (row) => {
  if (!row) return false;
  if (isAccessoryRow(row) || isPerSqftRow(row)) return hasRealPrice(row);
  return hasUsableCoverage(row) && hasRealPrice(row);
};

/**
 * IDI-373: rows offered in the AREA-PRICED product picker -- insulation by
 * category AND priceable by coverage. This is the composition callers want.
 * Declared after filterInsulationRows to avoid a TDZ reference.
 */

/** Rows destined for the insulation tenant-product merge: any other resolved category, active. */
export const filterInsulationRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter(
    (i) =>
      i &&
      i.categoryId &&
      i.categoryId !== 'accessories' &&
      i.categoryId !== 'light_covers' &&
      (i.status || 'active') === 'active'
  );

export const filterAreaPriceableRows = (rows = []) =>
  filterInsulationRows(rows).filter(isOfferable);

/**
 * IDI-373: accessories offered in the accessory picker -- price required,
 * coverage not. Composes with filterAccessoryRows (the category router).
 */
export const filterOfferableAccessoryRows = (rows = []) =>
  filterAccessoryRows(rows).filter(hasRealPrice);
